import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { printSheet, type SheetRow } from "@vocifuchs/shared";
import { api, ApiError } from "../lib/api.js";
import { common } from "../copy/common.js";
import { errors } from "../copy/errors.js";
import { lists } from "../copy/lists.js";
import { Banner, Button } from "../components/ui.js";
import { sheetPdf } from "../lib/sheetPdf.js";

interface PrintList {
  id: string;
  title: string;
  lang_b: "en" | "fr";
  items: Array<{ term_id: string; a: string; b: string }>;
}

const copy = lists.print;

/** A fresh order each time the page opens; the seed keeps sheet and solution together. */
const newSeed = () => Math.floor(Math.random() * 2 ** 32);

/**
 * A web app started from the iOS home screen: there `window.print()` does
 * nothing at all. The sheet goes out as a PDF through the share sheet
 * instead, which offers «Drucken». Only iOS has `navigator.standalone`;
 * Android prints from an installed app.
 */
const cannotPrint = () =>
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

/**
 * A list on paper (issue #102): German words with a line to write on, the
 * solution on page 2. The browser prints it or saves it as a PDF; nothing is
 * generated on the server and no new data leaves it.
 */
export function PrintScreen() {
  const { id } = useParams();
  const [list, setList] = useState<PrintList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [seed, setSeed] = useState(newSeed);

  useEffect(() => {
    if (!id) return;
    void api<PrintList>(`/api/lists/${id}`)
      .then(setList)
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : errors.failed));
  }, [id]);

  const rows = useMemo(() => (list ? printSheet(list.items, seed) : []), [list, seed]);

  // Made ahead, not on tap: the share sheet only opens straight from a tap,
  // and loading jsPDF first would use that tap up.
  const viaShare = cannotPrint() && typeof navigator.canShare === "function";
  const [pdf, setPdf] = useState<File | null>(null);
  const [shareFailed, setShareFailed] = useState(false);
  useEffect(() => {
    if (!viaShare || shareFailed || !list) return;
    let live = true;
    setPdf(null);
    void sheetPdf({
      title: list.title,
      heading: `${copy.directionPdf(common.lang[list.lang_b])} · ${common.words(rows.length)}`,
      solutionTitle: `${copy.solution} · ${list.title}`,
      footer: copy.footer,
      or: copy.or,
      rows,
    })
      .then((file) => {
        if (!live) return;
        if (navigator.canShare({ files: [file] })) setPdf(file);
        else setShareFailed(true);
      })
      .catch(() => live && setShareFailed(true));
    return () => {
      live = false;
    };
  }, [viaShare, shareFailed, list, rows]);

  const share = () => {
    if (!pdf) return;
    navigator.share({ files: [pdf] }).catch((err: unknown) => {
      // Closing the share sheet is not a failure, nor a second tap while it
      // is still open (InvalidStateError).
      if (err instanceof DOMException && (err.name === "AbortError" || err.name === "InvalidStateError")) return;
      setShareFailed(true);
    });
  };

  // No Shell here, so the error needs its own way out (docs/UX.md §1).
  if (error) {
    return (
      <div className="space-y-4">
        <Banner>{error}</Banner>
        <Link to="/" className="font-semibold text-fox-ink underline-offset-4 hover:underline">
          {common.backToStart}
        </Link>
      </div>
    );
  }
  if (!list) return <p className="text-ink-soft">{common.loading}</p>;

  const lang = list.lang_b === "en" ? "en-GB" : "fr-FR";
  const heading = (
    <p className="text-ink-soft print:text-black">
      {copy.direction(common.lang[list.lang_b])} · {common.words(rows.length)}
    </p>
  );

  return (
    <div className="space-y-6">
      <div className="space-y-4 print:hidden">
        <Link
          to={`/lists/${list.id}`}
          className="inline-flex items-center gap-1 font-semibold text-fox-ink underline-offset-4 hover:underline"
        >
          {copy.back}
        </Link>
        <div className="flex flex-col gap-3 sm:flex-row">
          {!cannotPrint() ? (
            <Button onClick={() => window.print()}>{copy.print}</Button>
          ) : viaShare && !shareFailed ? (
            <Button disabled={!pdf} onClick={share}>
              {copy.print}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={() => setSeed(newSeed())}>
            {copy.shuffle}
          </Button>
        </div>
        {!cannotPrint() ? (
          <p className="text-sm text-ink-soft">{copy.hint}</p>
        ) : viaShare && !shareFailed ? (
          <p className="text-sm text-ink-soft">{copy.hintShare}</p>
        ) : (
          // No print dialog here, so the usual hint about it would be wrong.
          <p className="font-semibold">{copy.noPrintHere}</p>
        )}
      </div>

      <section
        aria-labelledby="sheet-title"
        className="rounded-3xl border border-line/60 bg-paper-3 p-5 sm:p-8 print:rounded-none print:border-0 print:bg-transparent print:p-0"
      >
        <h1 id="sheet-title" className="text-3xl print:text-black">
          {list.title}
        </h1>
        {heading}
        <ol className="mt-6">
          {rows.map((row, i) => (
            <SheetLine key={i} row={row} />
          ))}
        </ol>
        <p className="mt-6 text-xs text-ink-soft print:text-black">{copy.footer}</p>
      </section>

      <section
        aria-labelledby="solution-title"
        className="rounded-3xl border border-line/60 bg-paper-3 p-5 sm:p-8 print:break-before-page print:rounded-none print:border-0 print:bg-transparent print:p-0"
      >
        <h2 id="solution-title" className="text-3xl print:text-black">
          {copy.solution} · {list.title}
        </h2>
        {heading}
        <ol className="mt-6">
          {rows.map((row, i) => (
            <SheetLine key={i} row={row} lang={lang} />
          ))}
        </ol>
        <p className="mt-6 text-xs text-ink-soft print:text-black">{copy.footer}</p>
      </section>
    </div>
  );
}

/**
 * One word and either a line to write on or, on the solution page, every
 * accepted answer. A row is never split across two pages, and its height
 * leaves room for a child's handwriting. On a phone the word sits above a
 * full-width line: two columns there would chop a long word into syllables.
 */
function SheetLine({ row, lang }: { row: SheetRow; lang?: string }) {
  return (
    <li className="grid min-h-12 grid-cols-1 gap-x-4 gap-y-1 py-2 break-inside-avoid sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] sm:items-end print:min-h-[12mm] print:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] print:items-end print:py-0">
      {/* A German compound must not run across the line: break it, with hyphens where the browser can. */}
      <span className="font-semibold break-words hyphens-auto print:text-black">{row.prompt}</span>
      {lang ? (
        <span className="break-words hyphens-auto print:text-black">
          {row.answers.map((answer, i) => (
            <span key={i}>
              {i > 0 ? <span className="text-ink-soft print:text-black"> {copy.or} </span> : null}
              <span lang={lang}>{answer}</span>
            </span>
          ))}
        </span>
      ) : (
        // Only a line to write on; a screen reader has nothing to read there.
        <span aria-hidden className="block h-8 border-b-2 border-ink-soft/60 sm:h-6 print:h-6 print:border-black" />
      )}
    </li>
  );
}
